import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { User, userServices } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { canManageRoles } from '../lib/permissions';

const UserDetail = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { id } = useParams<{ id: string }>();
  const { user: authUser, userRole } = useAuth();

  useEffect(() => {
    const fetchUser = async () => {
      if (!id) {
        setError('No user ID provided');
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await userServices.getById(id);
        if (error) throw error;
        setUser(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch user details');
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [id]);

  const handleRoleChange = async (newRole: 'view_only' | 'administrator' | 'superadmin') => {
    if (!user?.id) return;

    try {
      const { error } = await userServices.update(user.id, { role: newRole });
      if (error) throw error;
      setUser(prevUser => prevUser ? { ...prevUser, role: newRole } : null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user role');
    }
  };

  const getRoleBadgeClasses = (role: string) => {
    switch (role) {
      case 'superadmin':
        return 'bg-purple-100 text-purple-800';
      case 'administrator':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) return <div className="text-center p-4">Loading...</div>;
  if (error) return <div className="text-red-600 p-4">{error}</div>;
  if (!user) return <div className="text-center p-4">User not found</div>;

  const canManageUserRoles = canManageRoles(userRole) && authUser?.id !== user.id;

  return (
    <div className="p-6">
      <div className="mb-6">
        <Link to="/users" className="flex items-center text-blue-600 hover:text-blue-800">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Users
        </Link>
      </div>

      <div className="bg-white shadow rounded-lg mb-6">
        <div className="p-6">
          <h1 className="text-2xl font-bold mb-6">User Details</h1>
          
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <p className="text-sm font-medium text-gray-500">Full Name</p>
              <p className="mt-1 text-lg font-semibold">{user.name}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Username</p>
              <p className="mt-1">@{user.username}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Email</p>
              <p className="mt-1">{user.email}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Phone</p>
              <p className="mt-1">{user.phonenumber || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Role</p>
              <div className="mt-1 flex items-center">
                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadgeClasses(user.role)}`}>
                  {user.role}
                </span>
                {canManageUserRoles && (
                  <select
                    value={user.role}
                    onChange={(e) => handleRoleChange(e.target.value as 'view_only' | 'administrator' | 'superadmin')}
                    className="ml-4 block pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  >
                    <option value="view_only">View Only</option>
                    <option value="administrator">Administrator</option>
                    <option value="superadmin">Superadmin</option>
                  </select>
                )}
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Created</p>
              <p className="mt-1">{new Date(user.created_at).toLocaleDateString()}</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Additional sections like activity history could go here */}
    </div>
  );
};

export default UserDetail;
